import useMobile from "customHooks/useMobile";
import useRTL from "customHooks/useRTL";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { Children, useEffect, useState } from "react";
import SwipeableViews from "react-swipeable-views";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import styled from "styled-components";
import RoundedBadge from "./RoundedBadge";

type Props = {
  TabsItems: {
    label: string;
    badge?: number;
    icon?: string;
    minWidth?: number;
    fullWidth?: boolean;
    asHref?: string;
  }[];
  defaultIndex?: number;
  children?: JSX.Element[];
  ExtraButton?: JSX.Element;
  setActiveIndex?: number; // Add this line
  canSwipeTabs?: boolean;
  hrefTabs?: boolean;
};
const MolhamTabs = ({
  TabsItems,
  defaultIndex = 0,
  ExtraButton,
  setActiveIndex,
  children,
  canSwipeTabs = true,
  hrefTabs = false,
}: Props) => {
  const isRTL = useRTL();
  const isMobile = useMobile("md");
  const { t } = useTranslation("common");
  const [tabIndex, setTabIndex] = useState(defaultIndex);

  useEffect(() => {
    if (setActiveIndex === undefined || setActiveIndex === null) {
      return;
    }
    setTabIndex(setActiveIndex);
  }, [setActiveIndex]);
  const handleTabChange = (index) => {
    setTabIndex(index);
    const selectedTab = document.getElementById(`tab:r0:${index}`);
    selectedTab?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const handleSwipe = (i: number, f: number) => {
    if (!canSwipeTabs) {
      return;
    }
    const chLen = children?.length || 0;
    if (i > chLen - 1) {
      return;
    } else if (i < 0) {
      return;
    } else if (f > i) {
      handleTabChange(f - 1);
    } else if (f < i) {
      handleTabChange(f + 1);
    } else {
      handleTabChange(f);
    }
  };
  const { push } = useRouter();
  return (
    <CustomTabs
      isRTL={isRTL}
      focusTabOnClick={true}
      selectedIndex={tabIndex}
      onSelect={handleTabChange}
    >
      <TabList className="custom-tab-list">
        {TabsItems.map((item, index) => {
          const isLastTab = index === TabsItems.length - 1;

          return (
            <Tab
              className={`custom-tab d-flex align-items-center justify-content-center ${
                isLastTab ? "mx-0" : ""
              } ${item?.fullWidth ? "flex-grow-1" : ""} tab:${
                item.label
              }:${index}`}
              selectedClassName="custom-tab-selected"
              key={index}
              style={item?.minWidth ? { minWidth: item.minWidth } : {}}
              onClick={() => {
                if (hrefTabs) {
                  push(item.asHref);
                }
                handleTabChange(index);
                const selectedTab = document.getElementsByClassName(
                  `tab:${item.label}:${index}`
                )[0];
                selectedTab?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }}
            >
              <span
                className={`v-centered fw-bold ${isMobile ? "fs-12" : "fs-15"}`}
              >
                {item?.icon && (
                  <i className={`${item.icon} me-1 fs-lg mb-0 mb-lg-1`}></i>
                )}
                {t(item.label)}
                {!isNaN(Number(item?.badge)) && (
                  <RoundedBadge className="ms-2" data={item.badge} />
                )}
              </span>
            </Tab>
          );
        })}
      </TabList>
      {!hrefTabs && (
        <SwipeableViews
          index={tabIndex}
          onChangeIndex={handleSwipe}
          disabled={!canSwipeTabs}
          containerStyle={{}}
          axis={isRTL ? "x-reverse" : "x"}
          style={{
            direction: isRTL ? "rtl" : "ltr",
          }}
          dir={isRTL ? "rtl" : "ltr"}
        >
          {Children.map(children, (child, index) => {
            if (!child) return null;
            return (
              <TabPanel
                style={{ overflow: "hidden" }}
                dir={isRTL ? "rtl" : "ltr"}
                key={index}
              >
                {child}
              </TabPanel>
            );
          })}
        </SwipeableViews>
      )}
      {ExtraButton}
    </CustomTabs>
  );
};

export default MolhamTabs;

const CustomTabs = styled(Tabs)<{ isRTL: boolean }>`
  position: relative;
  .custom-tab-list {
    list-style-type: none;
    padding: 0;
    display: flex;
    border-bottom: 1px solid var(--light-gray);
    user-select: none;
    margin-bottom: 0;
    overflow-x: auto;
    white-space: nowrap;
    width: 100%;
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For IE and Edge */
  }

  .custom-tab-list::-webkit-scrollbar {
    display: none; /* Hide the scrollbar for WebKit browsers */
  }

  .custom-tab {
    cursor: pointer;
    padding: 12px 16px;
    margin-right: ${(props) => (props.isRTL ? "0" : "10px")};
    margin-left: ${(props) => (props.isRTL ? "10px" : "0")};
    position: relative;
    color: var(--text-white);
    font-weight: bold;
    transition: color 0.3s ease;
    outline-style: none !important;
  }

  .custom-tab-selected {
    color: var(--primary2);
    font-weight: bold;
    position: relative;
  }

  .custom-tab-selected::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: var(--primary2);
    transition: transform 0.3s ease;
  }
`;
